import './App.css';
import { useState } from 'react';
import { Box, useTheme, useMediaQuery } from "@mui/material";
import SensorParserConfig from './Pages/SensorParserConfig';
import GatewayParserConfig from './Pages/GatewayParserConfig';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import ParserDetail from './Pages/ParserDetail';
import ParserSimulate from './Pages/ParserSimulate';
import TopBar from "./components/Common/TopBar";
import Sidebar from "./components/Common/Sidebar";
import Device from './Pages/Device';
import Dashboard from './Pages/Dashboard';
import Settings from './Pages/Settings';
import Users from './Pages/Users';
import Roles from './Pages/Roles';
import Companies from './Pages/Companies';
import Departments from './Pages/Departments';
import DeviceGroups from './Pages/DeviceGroups';
import MyDeviceGroups from './Pages/MyDeviceGroups';
import Profile from './Pages/Profile';
import Login from './Pages/Login';
import SetPassword from './Pages/SetPassword';
import ForgotPassword from './Pages/ForgotPassword';
import { authService } from './services/authService';
import AuthProvider from './hoc/AuthProvider/AuthProvider';
import ProtectedPage from './Router/ProtectedPage';
import { Modules, Permissions } from './config/permissions';
import ErrorBoundary from './components/Common/ErrorBoundary';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  return authService.isAuthenticated() ? <>{children}</> : <Navigate to="/login" replace />;
}

function AppContent() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 900);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", backgroundColor: "#f0f2f5" }}>
      <TopBar
        sidebarOpen={sidebarOpen && !isMobile}
        onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
      />
      <Box sx={{ display: "flex", flex: 1 }}>
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <Box sx={{ flex: 1, overflow: "auto", p: { xs: 2, md: 3 }, minWidth: 0 }}>
          <ErrorBoundary>
          <Routes>
            <Route path="/" element={<Navigate to="/Dashboard" replace />} />
            <Route
              path="/Dashboard"
              element={
                <PrivateRoute>
                  <ProtectedPage permissions={Permissions.Dashboard_View} modules={Modules.IOT_CORE}>
                    <Dashboard />
                  </ProtectedPage>
                </PrivateRoute>
              }
            />
            <Route
              path="/SensorParsers"
              element={
                <PrivateRoute>
                  <ProtectedPage permissions={Permissions.ParserConfig_View} modules={Modules.IOT_CORE}>
                    <SensorParserConfig />
                  </ProtectedPage>
                </PrivateRoute>
              }
            />
            <Route
              path="/GatewayParsers"
              element={
                <PrivateRoute>
                  <ProtectedPage permissions={Permissions.ParserConfig_View} modules={Modules.IOT_CORE}>
                    <GatewayParserConfig />
                  </ProtectedPage>
                </PrivateRoute>
              }
            />
            <Route
              path="/ParserDetail"
              element={
                <PrivateRoute>
                  <ProtectedPage permissions={Permissions.ParserConfig_View} modules={Modules.IOT_CORE}>
                    <ParserDetail />
                  </ProtectedPage>
                </PrivateRoute>
              }
            />
            <Route
              path="/ParserSimulate"
              element={
                <PrivateRoute>
                  <ProtectedPage permissions={Permissions.Simulate_View} modules={Modules.IOT_CORE}>
                    <ParserSimulate />
                  </ProtectedPage>
                </PrivateRoute>
              }
            />
            <Route
              path="/Devices"
              element={
                <PrivateRoute>
                  <ProtectedPage permissions={Permissions.Device_View} modules={Modules.IOT_CORE}>
                    <Device />
                  </ProtectedPage>
                </PrivateRoute>
              }
            />
            <Route
              path="/Settings"
              element={
                <PrivateRoute>
                  <Settings />
                </PrivateRoute>
              }
            />
            <Route
              path="/Users"
              element={
                <PrivateRoute>
                  <ProtectedPage permissions={Permissions.User_View} modules={Modules.IOT_CORE}>
                    <Users />
                  </ProtectedPage>
                </PrivateRoute>
              }
            />
            <Route
              path="/Roles"
              element={
                <PrivateRoute>
                  <ProtectedPage permissions={Permissions.Role_View} modules={Modules.IOT_CORE}>
                    <Roles />
                  </ProtectedPage>
                </PrivateRoute>
              }
            />
            <Route
              path="/Companies"
              element={
                <PrivateRoute>
                  <ProtectedPage permissions={Permissions.Company_View} modules={Modules.IOT_CORE}>
                    <Companies />
                  </ProtectedPage>
                </PrivateRoute>
              }
            />
            <Route
              path="/Departments"
              element={
                <PrivateRoute>
                  <ProtectedPage permissions={Permissions.Department_View} modules={Modules.IOT_CORE}>
                    <Departments />
                  </ProtectedPage>
                </PrivateRoute>
              }
            />
            <Route
              path="/DeviceGroups"
              element={
                <PrivateRoute>
                  <ProtectedPage permissions={Permissions.DeviceGroup_View} modules={Modules.IOT_CORE}>
                    <DeviceGroups />
                  </ProtectedPage>
                </PrivateRoute>
              }
            />
            <Route
              path="/MyDeviceGroups"
              element={
                <PrivateRoute>
                  <ProtectedPage permissions={Permissions.Device_View} modules={Modules.IOT_CORE}>
                    <MyDeviceGroups />
                  </ProtectedPage>
                </PrivateRoute>
              }
            />
            <Route
              path="/Profile"
              element={
                <PrivateRoute>
                  <Profile />
                </PrivateRoute>
              }
            />
          </Routes>
          </ErrorBoundary>
        </Box>
      </Box>
    </Box>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/set-password/:token" element={<SetPassword />} />
          <Route path="/*" element={<AppContent />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
